import React from 'react';
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';
import { Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { KeyType, MAX_KEYS_PER_BATCH } from '../../types/keyRegister';

interface AddKeysModalProps {
    show: boolean;
    onHide: () => void;
    type: KeyType | null;
    /** The default start number: the type's highest numeric serial + 1 (`nextSerialStart`). */
    defaultStart: number;
    onSubmit: (count: number, startNumber: number) => Promise<void>;
    submitting: boolean;
}

interface AddKeysFormValues {
    count: string;
    startNumber: string;
}

const integer = (label: string, min: number, max?: number) => {
    let schema = Yup.number()
        .typeError(`${label} måste vara ett heltal`)
        .integer(`${label} måste vara ett heltal`)
        .min(min, `${label} måste vara minst ${min}`);
    if (max !== undefined) schema = schema.max(max, `Högst ${max} nycklar åt gången`);
    return schema.required(`${label} krävs`);
};

const schema = Yup.object({
    count: integer('Antal', 1, MAX_KEYS_PER_BATCH),
    startNumber: integer('Startnummer', 0),
});

/** "Lägg till exemplar": N keys numbered from a start number, each created with its `created` event. */
const AddKeysModal: React.FC<AddKeysModalProps> = ({ show, onHide, type, defaultStart, onSubmit, submitting }) => {
    const handleSubmit = async (values: AddKeysFormValues, helpers: FormikHelpers<AddKeysFormValues>) => {
        helpers.setStatus(undefined);
        try {
            await onSubmit(Number(values.count), Number(values.startNumber));
        } catch (err) {
            helpers.setStatus(err instanceof Error ? err.message : 'Kunde inte lägga till nycklar');
        }
    };

    return (
        <Modal show={show} onHide={onHide} centered>
            <Modal.Header closeButton>
                <Modal.Title>Lägg till exemplar</Modal.Title>
            </Modal.Header>
            <Formik<AddKeysFormValues>
                initialValues={{ count: '1', startNumber: String(defaultStart) }}
                validationSchema={schema}
                enableReinitialize
                onSubmit={handleSubmit}
            >
                {({ values, errors, touched, status, handleChange, handleBlur, handleSubmit: submitForm, setStatus }) => {
                    const count = Number(values.count);
                    const start = Number(values.startNumber);
                    const valid = Number.isInteger(count) && count >= 1 && count <= MAX_KEYS_PER_BATCH && Number.isInteger(start) && start >= 0;
                    return (
                        <Form noValidate onSubmit={submitForm}>
                            <Modal.Body>
                                {status && <Alert variant="danger" dismissible onClose={() => setStatus(undefined)}>{status}</Alert>}
                                {type && <p className="mb-3"><strong>{type.name}</strong></p>}
                                <Form.Group className="mb-3" controlId="add-keys-count">
                                    <Form.Label>Antal *</Form.Label>
                                    <Form.Control
                                        type="number"
                                        inputMode="numeric"
                                        min={1}
                                        max={MAX_KEYS_PER_BATCH}
                                        name="count"
                                        value={values.count}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        isInvalid={touched.count && !!errors.count}
                                        autoFocus
                                    />
                                    <Form.Control.Feedback type="invalid">{errors.count}</Form.Control.Feedback>
                                </Form.Group>
                                <Form.Group controlId="add-keys-start">
                                    <Form.Label>Startnummer *</Form.Label>
                                    <Form.Control
                                        type="number"
                                        inputMode="numeric"
                                        min={0}
                                        name="startNumber"
                                        value={values.startNumber}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        isInvalid={touched.startNumber && !!errors.startNumber}
                                    />
                                    <Form.Control.Feedback type="invalid">{errors.startNumber}</Form.Control.Feedback>
                                    <Form.Text className="text-muted">
                                        {valid
                                            ? count === 1 ? `Skapar nr ${start}.` : `Skapar nr ${start}–${start + count - 1}.`
                                            : 'Förvalt: typens högsta nummer + 1.'}
                                    </Form.Text>
                                </Form.Group>
                            </Modal.Body>
                            <Modal.Footer>
                                <Button variant="secondary" onClick={onHide} disabled={submitting}>Avbryt</Button>
                                <Button variant="primary" type="submit" disabled={submitting}>
                                    {submitting && <Spinner animation="border" size="sm" className="me-2" />}
                                    Lägg till
                                </Button>
                            </Modal.Footer>
                        </Form>
                    );
                }}
            </Formik>
        </Modal>
    );
};

export default AddKeysModal;
